import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from 'react';
import {
  Input, Card, Descriptions, Table, Button, Image, Row, Col, Grid, Space, Skeleton, Empty, Typography, Modal, Pagination, Popover, theme
} from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { PlayCircleOutlined, PlusOutlined, MinusOutlined, DownOutlined, UpOutlined, DeleteOutlined, FireTwoTone } from '@ant-design/icons';
import QtyChangerRow from '../QtyChangerRow';
import {
  clearPageQty,
  addToCart,
  removeFromCart,
} from '../../actions/cart';
import { applyBulkDiscount } from '../../functions/applyBulkDiscount';
import { gold } from '@ant-design/colors';
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";

const { Link, Title, Text } = Typography;
const { useToken } = theme;
const { useBreakpoint } = Grid;

const renderProductPrice = (text, product, index, qtyInCart = null) => {
  const basePrice = product.product_price;
  const salePrice = product.product_price_sale;
  const campaignPrice = product.product_price_campaign;
  const bulk = product.product_price_bulk;
  const quantity = qtyInCart ?? product.qtyInCart;

  const bulkPrice = bulk
    ? Object.entries(bulk).reduce((selected, [minQty, price], idx, arr) => {
      const nextMin = arr[idx + 1]?.[0];
      return quantity >= minQty && (!nextMin || quantity < nextMin) ? price : selected;
    }, null)
    : null;

  let promoPrice = null;
  if (campaignPrice != null && bulkPrice != null) {
    promoPrice = Math.min(campaignPrice, bulkPrice);
  } else if (campaignPrice != null) {
    promoPrice = campaignPrice;
  } else if (bulkPrice != null) {
    promoPrice = bulkPrice;
  }

  const finalPrice = promoPrice ?? salePrice ?? basePrice;
  const showStrike = finalPrice < basePrice;

  const tipColumns = [
    { title: 'Quantidade', dataIndex: 'quantity', key: 'quantity' },
    { title: 'Preço Unitário', dataIndex: 'value', key: 'value', render: value => `R$ ${value.toFixed(2)}` },
  ];

  const bulkDetails = bulk
    ? Object.entries(bulk).map(([minQty, value], idx, arr) => ({
      quantity: arr[idx + 1] ? `${minQty} a ${arr[idx + 1][0] - 1}` : `${minQty}+`,
      value,
    }))
    : [];

  return (
    <Space align="center" direction="vertical" style={{ gap: 0 }}>
      {showStrike && (
        <Text delete type="secondary" style={{ marginBottom: 0 }}>
          {`R$ ${basePrice.toFixed(2)}`}
        </Text>
      )}
      {bulkDetails.length > 0 ? (
        <Popover
          content={<Table dataSource={bulkDetails} columns={tipColumns} pagination={false} size="small" />}
          trigger="click"
          placement="topLeft"
        >
          <Space align="center" direction="horizontal" style={{ gap: 0 }}>
            <Button type="link" icon={<FireTwoTone twoToneColor={gold[5]} />} style={{ marginRight: 0 }} />
            <Title level={5} strong style={{ margin: 0, cursor: 'pointer' }}>
              {`R$ ${finalPrice.toFixed(2)}`}
            </Title>
          </Space>
        </Popover>
      ) : (
        <Title level={5} strong style={{ margin: 0 }}>
          {`R$ ${finalPrice.toFixed(2)}`}
        </Title>
      )}
    </Space>
  );
};


const ProductCard = React.memo(({ product, qtyInCart, updateFeaturedProduct, orderOutOfStock }) => {
  const dispatch = useDispatch();
  const screens = useBreakpoint();

  if (orderOutOfStock === null) {
    return null;
  }

  const imgSrc = product.product_image?.[0]?.url || '/fallback.png';

  const stock = orderOutOfStock ? 10000 : product.product_stock;
  const [qtyInPage, setQtyInPage] = useState(0);

  return (
    <Col xs={24} sm={12} md={8} lg={6}>
      <Card
        style={{
          marginBottom: 16,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            marginBottom: 16,
            height: 200,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Image
            preview={product.product_image?.[0] ? { visible: false } : false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              maxHeight: '200px',
            }}
            src={imgSrc}
            fallback="/fallback.png"
            onClick={() => updateFeaturedProduct(product)}
          />
        </div>
        <div
          style={{
            marginTop: 6,
            height: 48,
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Link onClick={() => updateFeaturedProduct(product)}>
            {product.product_name}
          </Link>
        </div>
        <div>
          <Row justify="space-between" align="middle" style={{ height: 38, marginBottom: 8 }}>
            <Col>
              {parseInt(product.product_stock) > 0
                ? `${product.product_stock} em estoque`
                : 'Sem estoque'}
            </Col>
            <Col>{renderProductPrice(null, product, null, qtyInCart)}</Col>
          </Row>
          <Row
            justify="space-between"
            align="middle"
            style={{ marginTop: 16, width: '100%' }}
          >
            <Col flex="auto">
              <Space.Compact size={screens.md ? 'middle' : 'large'}>
                <Button
                  onClick={() => {
                    setQtyInPage(qtyInPage - 1);
                  }}
                  disabled={stock <= 0 || qtyInPage <= 0}
                  icon={<MinusOutlined />}
                />
                <Input
                  onPressEnter={() => {
                    if (qtyInPage) {
                      dispatch(addToCart(product, qtyInPage, qtyInCart, qtyInPage));
                      dispatch({ type: 'SET_CART', updated: true, returned: false });
                      setQtyInPage(0);
                    }
                  }}
                  pattern="\d*"
                  onChange={(e) => {
                    setQtyInPage(parseInt(e.target.value.replace(/\D/g, '') | 0));
                  }}
                  style={{
                    width: 50,
                    textAlign: 'center',
                    borderRadius: 0,
                  }}
                  value={qtyInPage}
                  disabled={stock <= 0 || stock - qtyInCart <= 0}
                />
                <Button
                  onClick={() => {
                    setQtyInPage(qtyInPage + 1);
                  }}
                  disabled={stock - qtyInCart <= 0 || stock - qtyInCart - qtyInPage <= 0}
                  icon={<PlusOutlined />}
                />
              </Space.Compact>
            </Col>
            <Col>
              <Button
                type="primary"
                size={screens.md ? 'middle' : 'large'}
                onClick={() => {
                  dispatch(addToCart(product, qtyInPage, qtyInCart, qtyInPage));
                  dispatch({ type: 'SET_CART', updated: true, returned: false });
                  setQtyInPage(0);
                }}
              >
                Adicionar
              </Button>
            </Col>
          </Row>
          {qtyInCart > 0 && (
            <div style={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              margin: 8,
            }}>
              <Space size={2}>
                <Text>{`${qtyInCart} no Carrinho`}</Text>
                <Button size='small' onClick={() => {
                  dispatch(removeFromCart(product));
                  dispatch({ type: 'CART_UPDATED', updated: true });
                  dispatch({ type: 'CART_RETURNED', returned: false });
                }} icon={<DeleteOutlined />} type="link" />
              </Space>
            </div>
          )}
        </div>
      </Card>
    </Col>
  );
});



const ProductGallery = React.memo(
  ({
    images = [],
    videoSrc = null,
    videoThumbnail = null,
    thumbnailPosition = "left",
    height = "65vh",
    thumbSize = 70,
  }) => {
    const videoRef = useRef(null);
    const ytPlayer = useRef(null);

    const isYouTube = (url) => /youtu\.?be/.test(url);

    const items = useMemo(() => {
      const imgs = (images || []).map((x) => ({
        original: x.url,
        thumbnail: x.url,
        type: "image",
      }));
      if (videoSrc)
        return [
          {
            original: videoSrc,
            thumbnail: videoThumbnail || "video-placeholder",
            type: isYouTube(videoSrc) ? "youtube" : "video",
          },
          ...imgs,
        ];
      return imgs;
    }, [images, videoSrc, videoThumbnail]);

    useEffect(() => {
      if (isYouTube(videoSrc)) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

        window.onYouTubeIframeAPIReady = () => {
          ytPlayer.current = new window.YT.Player("yt-player", {
            events: {},
          });
        };
      }
    }, [videoSrc]);

    const handleSlide = (index) => {
      if (ytPlayer.current && ytPlayer.current.pauseVideo) {
        ytPlayer.current.pauseVideo();
      }
      if (videoRef.current && videoRef.current.pause) {
        try {
          videoRef.current.pause();
        } catch {}
      }
    };

    return (
      <ImageGallery
        showFullscreenButton={false}
        showPlayButton={false}
        items={items}
        showThumbnails
        thumbnailPosition={thumbnailPosition}
        onSlide={handleSlide}
        renderItem={(item) =>
          item.type === "video" ? (
            <div
              style={{
                position: "relative",
                width: "100%",
                height,
                overflow: "hidden",
                backgroundColor: "#000",
              }}
            >
              <video
                ref={videoRef}
                src={item.original}
                controls
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          ) : item.type === "youtube" ? (
            <div
              style={{
                position: "relative",
                width: "100%",
                height,
                overflow: "hidden",
                backgroundColor: "#000",
              }}
            >
              <iframe
                id="yt-player"
                width="100%"
                height="100%"
                src={item.original.replace("watch?v=", "embed/") + "?enablejsapi=1"}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          ) : (
            <Image
              width="auto"
              height={height}
              src={item.original}
              style={{ objectFit: "cover", cursor: "pointer" }}
              preview={{ mask: false }}
            />
          )
        }
        renderThumbInner={(item) => {
          if (item.type === "video" || item.type === "youtube") {
            return (
              <div
                style={{
                  width: thumbSize,
                  height: thumbSize,
                  borderRadius: 6,
                  overflow: "hidden",
                  position: "relative",
                  backgroundColor: "#f5f5f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {item.thumbnail && item.thumbnail !== "video-placeholder" ? (
                  <>
                    <img
                      src={item.thumbnail}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        filter: "brightness(0.9)",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PlayCircleOutlined
                        style={{
                          fontSize: thumbSize * 0.4,
                          color: "rgba(255,255,255,0.8)",
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: "rgba(0,0,0,0.05)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PlayCircleOutlined
                      style={{
                        fontSize: thumbSize * 0.4,
                        color: "rgba(0,0,0,0.45)",
                      }}
                    />
                  </div>
                )}
              </div>
            );
          }

          return (
            <div style={{ width: thumbSize, height: thumbSize }}>
              <img
                src={item.thumbnail}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          );
        }}
      />
    );
  }
);


const BuyerListProduct = React.memo(({ viewType }) => {
  const dispatch = useDispatch();
  const { token } = useToken();
  const screens = useBreakpoint();
  const inputRef = useRef(null);

  const [visible, setVisible] = useState(false);
  const [featuredProduct, setFeaturedProduct] = useState(null);
  const [imageUrls, setImageUrls] = useState([]);
  const [updatedProductList, setUpdatedProductList] = useState([]);
  const [rowOnFocus, setRowOnFocus] = useState(0);

  const productList = useSelector((state) => state.productList);
  const totalPages = useSelector((state) => state.pagination.totalPages);
  const page = useSelector((state) => state.pagination.page);
  const perPage = useSelector((state) => state.pagination.perPage);
  const sortColumn = useSelector(state => state.pagination.column);
  const sortOrder = useSelector(state => state.pagination.order);
  const cartProducts = useSelector((state) => state.cart.products);
  const pageQty = useSelector((state) => state.pageQty);
  const onlineStatus = useSelector((state) => state.sync.onlineStatus);
  const syncStatus = useSelector((state) => state.sync.syncStatus);
  const searchOnFocus = useSelector((state) => state.searchState.onFocus);
  const discountRules = useSelector((state) => state.rules.discountRules);
  const orderOutOfStock = useSelector((state) => state.orderOutOfStock.orderOutOfStock);

  const [productLoading, setProductLoading] = useState(null);
  const [activeTabKey2, setActiveTabKey2] = useState("description");

  const updateFeaturedProduct = useCallback((product) => {
    setFeaturedProduct(product);
  }, []);

  useEffect(() => setActiveTabKey2(screens.md ? "description" : "image"), [screens.md]);


  const isElementVisible = useCallback((el) => {
    const rect = el.getBoundingClientRect();
    const vWidth = window.innerWidth || document.documentElement.clientWidth;
    const vHeight = window.innerHeight || document.documentElement.clientHeight;
    if (
      rect.right < 0
      || rect.bottom < 0
      || rect.left > vWidth
      || rect.top > vHeight
    ) return false;
  }, []);

  const handleFocus = useCallback(() => {
    const { input } = inputRef.current;
    input.focus({ preventScroll: isElementVisible(input) });
    input.selectionStart = input.selectionEnd = input.value.length;
  }, [inputRef, isElementVisible]);

  useEffect(() => {
    productLoading === false && setRowOnFocus(0);
  }, [productLoading]);

  const ImageComp = useCallback((props) => {
    let imgSrc = null;

    if (onlineStatus) {
      if (props.product.product_image && props.product.product_image[0]) {
        imgSrc = props.product.product_image[0].thumbnail;
      }
    } else if (onlineStatus === false && syncStatus === 'synced') {
      imgSrc = localStorage.getItem('products' + `_${props.product.id}`);
    }

    return (
      <div style={{
        height: 60,
        width: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: 2
      }}>
        <Image
          preview={
            props.product.product_image && props.product.product_image[0]
              ? { visible: false }
              : false
          }
          style={{ maxWidth: 60, maxHeight: 60, objectFit: 'contain' }}
          src={imgSrc || '/fallback.png'}
          onClick={() => { setFeaturedProduct(props.product); }}
        />
      </div>
    );
  }, [onlineStatus, syncStatus]);

  const updateCart = useCallback((product, qtyInPage, qtyInCart) => {
    dispatch(addToCart(product, qtyInPage, qtyInCart, qtyInPage));
    dispatch({ type: 'SET_CART', updated: true, returned: false });
    dispatch(clearPageQty(product));
  }, [dispatch]);

  const smallColumns = useMemo(() => [
    {
      title: 'Produtos',
      render: (text, product, index) => {
        const qtyInPage = pageQty[product.id] || 0;
        return (
          <>
            <Link style={{ marginTop: 4 }} onClick={() => setFeaturedProduct(product)}
            >{product.product_name}</Link>
            <Row justify="space-between" align="middle">
              <Col>
                <>{`Cod.: ${product.product_sku}`}</>
              </Col>
              <Col>
                <>
                  {parseInt(product.product_stock) > 0
                    ? `${product.product_stock} em estoque`
                    : 'Sem estoque'}
                </>
              </Col>
            </Row>
            <Row justify="space-between" align="middle">
              <Col>
                <ImageComp product={product} />
              </Col>
              <Col>
                {renderProductPrice(text, product)}
              </Col>
            </Row>
            <Row justify="space-between" align="middle" style={{ marginBottom: 18 }}>
              <Col>
                <QtyChangerRow
                  updateCart={updateCart}
                  product={product}
                  qtyInCart={product.qtyInCart}
                  rowOnFocus={rowOnFocus}
                  setRowOnFocus={setRowOnFocus}
                  myIndex={index}
                  inputRef={inputRef}
                  handleFocus={handleFocus}
                  searchOnFocus={searchOnFocus}
                  orderOutOfStock={orderOutOfStock}
                />
              </Col>
              <Col>
                <Button
                  type="primary"
                  onClick={() => {
                    updateCart(product, qtyInPage, product.qtyInCart);
                  }}
                >
                  Adicionar
                </Button>
              </Col>
              {product.qtyInCart > 0 && (<div style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                marginBottom: 4,
              }}>
                <Space size={2}>
                  <Text>{`${product.qtyInCart} no Carrinho`}</Text>
                  <Button size='small' onClick={() => {
                    dispatch(removeFromCart(product));
                    dispatch({ type: 'CART_UPDATED', updated: true });
                    dispatch({ type: 'CART_RETURNED', returned: false });
                  }} icon={<DeleteOutlined />} type="link" />
                </Space>
              </div>)}
            </Row>
          </>);
      },
    },
  ], [pageQty, rowOnFocus, inputRef, handleFocus, searchOnFocus, orderOutOfStock, updateCart, dispatch, ImageComp]);


  const bigColumns = useMemo(() => [
    {
      title: 'Imagem',
      dataIndex: 'product_image',
      key: 'product_image',
      align: 'center',
      render: (text, product) => <ImageComp product={product} />,
      shouldCellUpdate: (record, prevRecord) => !_.isEqual(record.id, prevRecord.id)
    },
    {
      title: 'Descrição',
      sorter: () => { },
      sortOrder: sortColumn === 'Descrição' ? sortOrder : null,
      dataIndex: 'product_name',
      key: 'product_name',
      style: { cursor: "pointer" },
      render: (text, product) => (
        <Link onClick={() => setFeaturedProduct(product)}>{text}</Link>
      ),
      ellipsis: true,
      width: '30%',
      shouldCellUpdate: (record, prevRecord) => !_.isEqual(record.id, prevRecord.id)
    },
    {
      title: 'Código',
      sorter: () => { },
      sortOrder: sortColumn === 'Código' ? sortOrder : null,
      dataIndex: 'product_sku',
      key: 'product_sku',
      align: 'center',
      shouldCellUpdate: (record, prevRecord) => !_.isEqual(record.id, prevRecord.id)
    },
    {
      title: 'Estoque',
      dataIndex: 'product_stock',
      render(text) {
        const stock = parseInt(text, 10);
        return stock > 0
          ? { children: text }
          : { props: { style: { backgroundColor: token.colorErrorBgHover } }, children: 'Sem estoque' };
      },
      key: 'product_stock',
      align: 'center',
      shouldCellUpdate: (record, prevRecord) => !_.isEqual(record.product_stock, prevRecord.product_stock)
    },
    {
      title: 'Preço',
      sorter: () => { },
      sortOrder: sortColumn === 'Preço' ? sortOrder : null,
      dataIndex: 'product_price',
      key: 'product_price',
      render: renderProductPrice,
      align: 'center',
      shouldCellUpdate: (record, prevRecord) => !_.isEqual(record.qtyInCart, prevRecord.qtyInCart),
      width: 150,
    },
    {
      title: 'Quantidade',
      render: (text, product, index) => (
        <QtyChangerRow
          updateCart={updateCart}
          product={product}
          qtyInCart={product.qtyInCart}
          rowOnFocus={rowOnFocus}
          setRowOnFocus={setRowOnFocus}
          myIndex={index}
          inputRef={inputRef}
          handleFocus={handleFocus}
          searchOnFocus={searchOnFocus}
          orderOutOfStock={orderOutOfStock}
        />
      ),
      align: 'center',
    },
    {
      render(text, product) {
        const qtyInPage = pageQty[product.id] || 0;
        return {
          children: (
            <>
              <Button
                type="primary"
                onClick={() => {
                  updateCart(product, qtyInPage, product.qtyInCart);
                }}
              >
                Adicionar
              </Button>
              {
                product.qtyInCart > 0 && (
                  <div style={{
                    position: 'absolute',
                    left: '50%',
                    bottom: 0,
                    marginBottom: 3,
                    width: 150,
                    transform: 'translateX(-50%)',
                  }}>
                    <Space size={2}>
                      <Text>{`${product.qtyInCart} no Carrinho`}</Text>
                      <Button size='small' onClick={() => {
                        dispatch(removeFromCart(product));
                        dispatch({ type: 'CART_UPDATED', updated: true });
                        dispatch({ type: 'CART_RETURNED', returned: false });
                      }} icon={<DeleteOutlined />} type="link" />
                    </Space>
                  </div>
                )
              }
            </>
          ),
          width: 150,
        };
      },
      width: 150,
      align: 'center',
    },
  ], [ImageComp, sortColumn, sortOrder, token, updateCart, pageQty, rowOnFocus, inputRef, handleFocus, searchOnFocus, orderOutOfStock, dispatch]);

  const getCartValues = useCallback((productId) => {
    if (cartProducts) {
      const filteredCart = cartProducts.filter((cartObj) => productId === cartObj.id);
      const qtyInCart = filteredCart[0] ? filteredCart[0].quantity : 0;
      return qtyInCart;
    } else return 0;
  }, [cartProducts]);

  const cartQuantities = useMemo(() => {
    const quantities = {};
    if (cartProducts?.length) {
      cartProducts.forEach(product => {
        quantities[product.id] = product.quantity;
      });
    }
    return quantities;
  }, [cartProducts]);

  useEffect(() => {

    if (productList.loading) {
      setProductLoading(true);
    } else if (cartProducts && productLoading !== null && discountRules !== null) {
      const updatedList = Array.isArray(productList?.list)
        ? productList.list.map((product) => ({
          ...product,
          qtyInCart: getCartValues(product.id),
        }))
        : [];

      const productsRuleApplied = applyBulkDiscount(discountRules, updatedList);
      setUpdatedProductList(productsRuleApplied);
      setProductLoading(false);
    }
  }, [productList, productLoading, cartProducts, discountRules]);

  function handleCancel() {
    setActiveTabKey2(screens.md ? 'description' : 'image');
    setFeaturedProduct(null);
  }

  return (
    <>
      {viewType === 'list' ?
        <div style={{ margin: 4, minHeight: '60vh' }}>
          <Table
            columns={screens.lg ? bigColumns : smallColumns}
            dataSource={(productList.loading || productLoading !== false) ? [] : updatedProductList}
            locale={{
              emptyText: (productList.loading || productLoading !== false) ? <Skeleton active /> :
                <Empty description={
                  <Text>
                    Nenhum produto encontrado
                  </Text>
                } />,
            }}
            showSorterTooltip={false}
            sortDirections={['ascend', 'descend']}
            onRow={(record, rowIndex) => ({
              onClick: (event) => {
                if (screens.md && !event.target.closest('.edit-price-area')) {
                  if (rowIndex !== rowOnFocus) {
                    setRowOnFocus(rowIndex);
                  } else {
                    handleFocus();
                  }
                }
              },
              style: {
                backgroundColor: rowIndex === rowOnFocus ? token.colorBgLayout : 'transparent',
              },
            })}
            pagination={{
              pageSizeOptions: [12, 24, 60],
              position: ['bottomCenter'],
              current: page,
              total: totalPages * perPage,
              pageSize: perPage,
            }}
            expandable={{
              expandIconColumnIndex: 8,
              expandedRowRender: (product) => (product.gtin ? (
                <>{`GTIN: ${product.gtin}`}</>
              ) : (
                <>GTIN não registrado</>
              )),
              expandIcon: ({ expanded, onExpand, record }) => (expanded ? (
                <UpOutlined onClick={(e) => onExpand(record, e)} />
              ) : (
                <DownOutlined onClick={(e) => onExpand(record, e)} />
              )),
            }}
            onChange={(val, filters, sorter) => {
              setRowOnFocus(0);
              dispatch({
                type: 'SET_PAGINATION',
                page: val.current,
                perPage: val.pageSize,
                pageUpdated: false,
                column: sorter.column ? sorter.column.title : null,
                order: sorter.order ? sorter.order : null,
              });
            }}
          />
        </div> :
        <div style={{ margin: 8, minHeight: '60vh' }}>
          {(productList.loading || productLoading !== false) ? <div style={{
            borderRadius: token.borderRadius,
            padding: token.padding,
            backgroundColor: token.colorBgBase,
          }}>
            <Row gutter={[16, 16]}>
              {Array(4).fill(
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Card
                    style={{
                      marginBottom: 16,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* Imagem */}
                    <div
                      style={{
                        textAlign: 'center',
                        marginBottom: 16,
                        height: 200,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Skeleton.Image active style={{ width: '100%', height: '100%', maxHeight: 200 }} />
                    </div>
                    <div
                      style={{
                        marginTop: 8,
                        marginBottom: 8,
                        height: 48,
                        overflow: 'hidden',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <Skeleton.Node active style={{ width: 150, height: 150 }} />
                    </div>
                  </Card>
                </Col>
              )}
            </Row>
          </div> :
            updatedProductList.length > 0 ?
              <>
                <Row gutter={[8, 8]}>
                  {updatedProductList.map((product) => {
                    const qtyInCart = cartQuantities[product.id] || 0;

                    return (
                      <ProductCard
                        key={product.id}
                        product={product}
                        qtyInCart={qtyInCart}
                        updateFeaturedProduct={updateFeaturedProduct}
                        orderOutOfStock={orderOutOfStock}
                      />
                    );
                  })}
                </Row>
                <Pagination
                  align="center"
                  style={{ padding: 10 }}
                  current={page}
                  total={totalPages * perPage}
                  pageSize={perPage}
                  pageSizeOptions={[12, 24, 60]}
                  onChange={(newPage, newPerPage) => {
                    dispatch({
                      type: 'SET_PAGINATION',
                      page: newPage ? newPage : page,
                      perPage: newPerPage ? newPerPage : perPage,
                      pageUpdated: false,
                    });
                  }}
                />
              </> :
              <div style={{
                borderRadius: token.borderRadius,
                padding: token.padding,
                margin: token.margin,
                backgroundColor: token.colorBgBase,
              }}>
                <Empty description={
                  <Text>
                    Nenhum produto encontrado
                  </Text>
                } />
              </div>
          }
        </div>
      }
      <div style={{ display: 'none' }}>
        <Image.PreviewGroup
          preview={{
            visible,
            onVisibleChange: (vis) => {
              setVisible(vis);
              ~vis && setImageUrls([]);
            },
          }}
        >
          {imageUrls.map((x, index) => (
            <Image key={index} src={x} />
          ))}
        </Image.PreviewGroup>
      </div>
      <Modal
        destroyOnHidden
        centered
        width="95%"
        footer={
          <Button key="back" onClick={handleCancel}>
            Fechar
          </Button>
        }
        title={featuredProduct?.product_name}
        open={featuredProduct !== null}
        onCancel={handleCancel}
        styles={{
          body: {
            overflowY: "auto",
            overflowX: "hidden",
            padding: 0,
            margin: 0,
          },
        }}
      >
        <Row gutter={[16, 16]}>
          {screens.md && (<Col xs={24} md={12}>

            <Card size='small'>
              <ProductGallery
                images={featuredProduct?.product_image || []}
                videoSrc={featuredProduct?.video_url}
                thumbnailPosition="left"
                height="65vh"
                thumbSize={70}
              />
            </Card>
          </Col>)}
          <Col xs={24} md={12}>
            <Card
              size='small'
              style={{
                width: "100%",
                height: "100%",
              }}
              tabList={screens.md
                ? [
                  { key: "description", tab: "Descrição" },
                  { key: "details", tab: "Detalhes" },
                ] : [
                  { key: "image", tab: "Mídia" },
                  { key: "description", tab: "Descrição" },
                  { key: "details", tab: "Detalhes" },
                ]}
              activeTabKey={activeTabKey2}
              onTabChange={setActiveTabKey2}
              tabProps={{
                size: "large",
              }}
            >
              {activeTabKey2 === "image" && !screens.md && (
                <ProductGallery
                  images={featuredProduct?.product_image || []}
                  videoSrc={featuredProduct?.video_url}
                  videoThumbnail={featuredProduct?.video_thumbnail}
                  thumbnailPosition="bottom"
                  height="50vh"
                  thumbSize={70}
                />
              )}
              {activeTabKey2 === "description" && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: featuredProduct?.description
                      ? /<.*?>.*<\/.*?>/.test(featuredProduct.description)
                        ? featuredProduct.description
                        : featuredProduct.description.replace(/\n/g, "<br />")
                      : "Produto sem descrição",
                  }}
                  style={{
                    height: "45vh",
                    overflowY: "auto",
                  }}
                />
              )}
              {activeTabKey2 === "details" && (
                <Descriptions bordered column={1} size="middle">
                  <Descriptions.Item label="Código">{featuredProduct?.product_sku}</Descriptions.Item>
                  <Descriptions.Item label="Preço">
                    {featuredProduct?.product_price_sale
                      ? `R$ ${featuredProduct.product_price_sale.toFixed(2)} (Promoção)`
                      : `R$ ${featuredProduct?.product_price.toFixed(2)}`}
                  </Descriptions.Item>
                  <Descriptions.Item label="Estoque">{featuredProduct?.product_stock}</Descriptions.Item>
                  <Descriptions.Item label="Dimensões">
                    {featuredProduct?.length}cm x {featuredProduct?.width}cm x {featuredProduct?.height}cm
                  </Descriptions.Item>
                  <Descriptions.Item label="Peso">{featuredProduct?.weight} kg</Descriptions.Item>
                  <Descriptions.Item label="Volume">{featuredProduct?.volume} cm³</Descriptions.Item>
                </Descriptions>
              )}
            </Card>
          </Col>
        </Row>
      </Modal>
    </>
  );
});

export default BuyerListProduct;